import type { ArtworkDraftSnapshot } from "./artwork-controller";
import type { ConfiguratorDraftSnapshot } from "./configurator-controller";
import type { Clock, Scheduler } from "./pricing-controller";

export const PVC_DRAFT_VERSION = 1 as const;

export type DraftOrderContext =
  | { type: "create" }
  | { type: "edit"; orderLineId: string; orderLineVersion: number };

export type PvcProductDraft = {
  draftVersion: typeof PVC_DRAFT_VERSION;
  productSlug: "pvc-sign";
  /** Informational only. A mismatch is reconciled against the current schema. */
  productSchemaVersion: number;
  configurator: ConfiguratorDraftSnapshot;
  artwork: ArtworkDraftSnapshot;
  orderContext: DraftOrderContext;
  savedAt: string;
};

/**
 * Intentionally absent from PvcProductDraft:
 * PricePreview, configurationHash, pricingVersion, quoteExpiresAt,
 * clientRevision, requestFingerprint, filename and artwork warnings.
 */
export type PvcDraftSourceSnapshot = Omit<PvcProductDraft, "draftVersion" | "productSlug" | "savedAt">;

export interface DraftStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type DraftLoadResult =
  | { type: "none" }
  | { type: "restored"; draft: PvcProductDraft }
  | {
      type: "discarded";
      reason: "CORRUPT" | "UNSUPPORTED_DRAFT_VERSION" | "WRONG_PRODUCT";
    };

export type DraftState = {
  status: "idle" | "dirty" | "saved" | "error";
  lastSavedAt: number | null;
  /**
   * UI may show “✓ נשמר במכשיר זה” when this changes. It is rate-limited
   * separately from writes so continuous typing does not spam the customer.
   */
  saveNoticeRevision: number;
};

export interface DraftController {
  getState(): DraftState;
  load(): DraftLoadResult;
  markDirty(): void;
  flush(): boolean;
  clear(): void;
  subscribe(listener: (state: DraftState) => void): () => void;
  dispose(): void;
}

const defaultClock: Clock = { now: () => Date.now() };
const defaultScheduler: Scheduler = {
  setTimeout(callback, delayMs) { return globalThis.setTimeout(callback, delayMs); },
  clearTimeout(id) { globalThis.clearTimeout(id as ReturnType<typeof setTimeout>); },
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function parseDraft(raw: string): DraftLoadResult {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return { type: "discarded", reason: "CORRUPT" };
  }

  if (!isPlainObject(value)) return { type: "discarded", reason: "CORRUPT" };
  if (value.productSlug !== "pvc-sign") return { type: "discarded", reason: "WRONG_PRODUCT" };
  if (value.draftVersion !== PVC_DRAFT_VERSION) {
    return { type: "discarded", reason: "UNSUPPORTED_DRAFT_VERSION" };
  }

  const configurator = value.configurator;
  const artwork = value.artwork;
  const orderContext = value.orderContext;

  if (
    typeof value.productSchemaVersion !== "number" ||
    !Number.isInteger(value.productSchemaVersion) ||
    !isPlainObject(configurator) ||
    typeof configurator.widthRaw !== "string" ||
    typeof configurator.heightRaw !== "string" ||
    typeof configurator.quantityRaw !== "string" ||
    !isNullableString(configurator.materialId) ||
    !Array.isArray(configurator.addonIds) ||
    !configurator.addonIds.every(item => typeof item === "string") ||
    !isNullableString(configurator.requestedDate) ||
    !isPlainObject(artwork) ||
    !isNullableString(artwork.artworkMode) ||
    !isNullableString(artwork.artworkFileId) ||
    !isNullableString(artwork.uploadSessionId) ||
    !isPlainObject(orderContext) ||
    (orderContext.type !== "create" && orderContext.type !== "edit") ||
    typeof value.savedAt !== "string"
  ) {
    return { type: "discarded", reason: "CORRUPT" };
  }

  if (
    orderContext.type === "edit" &&
    (typeof orderContext.orderLineId !== "string" ||
      typeof orderContext.orderLineVersion !== "number" ||
      !Number.isInteger(orderContext.orderLineVersion))
  ) {
    return { type: "discarded", reason: "CORRUPT" };
  }

  const mode = artwork.artworkMode;
  if (
    mode !== null &&
    mode !== "upload-now" &&
    mode !== "send-later" &&
    mode !== "request-upload-link" &&
    mode !== "need-design"
  ) {
    return { type: "discarded", reason: "CORRUPT" };
  }

  const draft: PvcProductDraft = {
    draftVersion: PVC_DRAFT_VERSION,
    productSlug: "pvc-sign",
    productSchemaVersion: value.productSchemaVersion,
    configurator: {
      widthRaw: configurator.widthRaw,
      heightRaw: configurator.heightRaw,
      quantityRaw: configurator.quantityRaw,
      materialId: configurator.materialId,
      addonIds: [...configurator.addonIds],
      requestedDate: configurator.requestedDate,
    },
    artwork: {
      artworkMode: mode,
      artworkFileId: artwork.artworkFileId,
      uploadSessionId: artwork.uploadSessionId,
    },
    orderContext: orderContext.type === "create"
      ? { type: "create" }
      : {
          type: "edit",
          orderLineId: orderContext.orderLineId as string,
          orderLineVersion: orderContext.orderLineVersion as number,
        },
    savedAt: value.savedAt,
  };

  return { type: "restored", draft };
}

export function createDraftController(options: {
  storage: DraftStorage;
  getSnapshot: () => PvcDraftSourceSnapshot;
  storageKey?: string;
  scheduler?: Scheduler;
  clock?: Clock;
  saveDebounceMs?: number;
  noticeMinIntervalMs?: number;
}): DraftController {
  const storage = options.storage;
  const getSnapshot = options.getSnapshot;
  const key = options.storageKey ?? "happyprint:pvc-sign:draft:v1";
  const scheduler = options.scheduler ?? defaultScheduler;
  const clock = options.clock ?? defaultClock;
  const saveDebounceMs = options.saveDebounceMs ?? 400;
  const noticeMinIntervalMs = options.noticeMinIntervalMs ?? 3_000;

  let state: DraftState = {
    status: "idle",
    lastSavedAt: null,
    saveNoticeRevision: 0,
  };
  let disposed = false;
  let timer: unknown | null = null;
  let lastNoticeAt: number | null = null;
  const listeners = new Set<(state: DraftState) => void>();

  function emit(next: DraftState): void {
    if (disposed) return;
    state = next;
    for (const listener of listeners) listener(state);
  }

  function clearTimer(): void {
    if (timer !== null) {
      scheduler.clearTimeout(timer);
      timer = null;
    }
  }

  function writeNow(): boolean {
    if (disposed) return false;

    const now = clock.now();
    const snapshot = getSnapshot();
    const draft: PvcProductDraft = {
      draftVersion: PVC_DRAFT_VERSION,
      productSlug: "pvc-sign",
      productSchemaVersion: snapshot.productSchemaVersion,
      configurator: {
        ...snapshot.configurator,
        addonIds: [...snapshot.configurator.addonIds],
      },
      artwork: { ...snapshot.artwork },
      orderContext: snapshot.orderContext.type === "create"
        ? { type: "create" }
        : { ...snapshot.orderContext },
      savedAt: new Date(now).toISOString(),
    };

    try {
      storage.setItem(key, JSON.stringify(draft));
    } catch {
      emit({ ...state, status: "error" });
      return false;
    }

    const mayNotify = lastNoticeAt === null || now - lastNoticeAt >= noticeMinIntervalMs;
    if (mayNotify) lastNoticeAt = now;

    emit({
      status: "saved",
      lastSavedAt: now,
      saveNoticeRevision: state.saveNoticeRevision + (mayNotify ? 1 : 0),
    });
    return true;
  }

  function load(): DraftLoadResult {
    if (disposed) return { type: "none" };
    let raw: string | null;
    try {
      raw = storage.getItem(key);
    } catch {
      emit({ ...state, status: "error" });
      return { type: "none" };
    }
    if (raw === null) return { type: "none" };

    const parsed = parseDraft(raw);
    if (parsed.type === "discarded") {
      try { storage.removeItem(key); } catch { /* best effort cleanup */ }
    }
    return parsed;
  }

  function markDirty(): void {
    if (disposed) return;
    emit({ ...state, status: "dirty" });
    clearTimer();
    timer = scheduler.setTimeout(() => {
      timer = null;
      writeNow();
    }, saveDebounceMs);
  }

  function flush(): boolean {
    if (disposed) return false;
    clearTimer();
    return writeNow();
  }

  function clear(): void {
    if (disposed) return;
    clearTimer();
    try {
      storage.removeItem(key);
      emit({ status: "idle", lastSavedAt: null, saveNoticeRevision: state.saveNoticeRevision });
    } catch {
      emit({ ...state, status: "error" });
    }
  }

  return {
    getState: () => state,
    load,
    markDirty,
    flush,
    clear,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      clearTimer();
      listeners.clear();
    },
  };
}
